/**
 * @file Test mode (the "killswitch") — one SHARED mock sandbox.
 *
 * When an admin flips test mode ON, the whole app reads and writes the
 * shared sandbox namespace instead of production:
 *
 *   production collection  →  test_<collection>
 *
 * The sandbox is deliberately SHARED across all admins (mock data at the
 * ready, no per-entry seeding): two admins testing at once see — and can
 * overwrite — the same data. That's fine and even useful; the Reseed
 * button in the test banner rebuilds the whole thing for everyone.
 *
 * Properties that make this safe by construction:
 *   • Per-browser flag. It lives in localStorage, so one admin testing
 *     never flips anyone else into test mode — only the DATA is shared.
 *   • Monotonic prefix. `testCollectionName()` can only ever ADD the
 *     `test_` prefix; it can never name a production collection while
 *     test mode is on. A test-mode read/write is therefore structurally
 *     incapable of touching production data. (No production collection
 *     starts with `test_` — `testing_devices` doesn't match: no underscore.)
 *   • Admin-only. Firestore rules gate every `test_*` collection behind
 *     isAdmin(), so a non-admin flipping localStorage by hand reads nothing.
 *   • Slack + other side-effects are neutralised server-side (callables
 *     receive testMode and no-op the Slack transport — see api.js /
 *     slack-client.js), so a tester can never ping Finance.
 */

const LS_KEY = 'gear.testMode';
const listeners = new Set();

let testModeOn = readInitial();

function readInitial() {
  try {
    return globalThis.localStorage?.getItem(LS_KEY) === '1';
  } catch {
    return false;
  }
}

/** True iff the current browser session is in test mode. */
export function isTestMode() {
  return testModeOn;
}

/**
 * Flip test mode. Persists to localStorage and notifies subscribers
 * (main.js re-renders the whole app so every page re-mounts against the
 * new namespace). Caller is responsible for admin-gating the toggle UI;
 * a non-admin who flips localStorage by hand still hits Firestore rules
 * that deny test_* access to non-admins.
 */
export function setTestMode(on) {
  testModeOn = !!on;
  try {
    if (testModeOn) globalThis.localStorage?.setItem(LS_KEY, '1');
    else globalThis.localStorage?.removeItem(LS_KEY);
  } catch { /* private mode / quota — flag still lives in-memory */ }
  for (const cb of listeners) {
    try { cb(testModeOn); } catch (err) { console.error('[test-mode] listener error', err); }
  }
}

export function onTestModeChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Resolve a logical collection name to the namespace for the current
 * mode. Production: the name unchanged. Test mode: `test_<name>` (the
 * shared sandbox). THE single chokepoint — every client collection
 * reference must go through this (db.js does; the few direct readers
 * import it too).
 */
export function testCollectionName(name) {
  return testModeOn ? `test_${name}` : name;
}
